/**
 * Builds the `set_block_trade_context` payload — the visible UnifiedRfqs
 * trade-tape snapshot the AI agent uses to answer block-trade questions.
 * Shared by the background pump (`BlockTradeContextPump`, debounced) and the
 * `/analyze` drain (`PendingMessageDrain`, which pushes it synchronously
 * before the prompt so the agent has the tape when it answers).
 */

/** Cap the snapshot so the LLM context stays tight. */
export const MAX_TRADES = 50;

/**
 * Byte budget for the serialized payload. The gateway-proxy hard-closes the
 * client connection (status 1009, no response frame) on any message over its
 * read limit — `WS_MAX_CLIENT_MESSAGE_SIZE`, 32 KiB by default (dime-terminal
 * `api/terminal/gateway-proxy/config/config.go`). A killed connection also
 * swallows the "unknown method" rejection the pump latches on, so an
 * oversized snapshot turns into a permanent reconnect loop. 24 KiB leaves
 * headroom for the request envelope and for ops tightening the proxy limit.
 */
export const MAX_PAYLOAD_BYTES = 24 * 1024;

/** UTF-8 size of a string (falls back to code units where TextEncoder is absent). */
function byteLength(s: string): number {
  return typeof TextEncoder === 'undefined'
    ? s.length
    : new TextEncoder().encode(s).length;
}

/**
 * Greedily keep trades (newest-first) until the next one would exceed the
 * byte budget for their serialized JSON, so a snapshot stays a contiguous
 * head of the tape. Unserializable or missing entries are skipped, never
 * thrown on. Shared by the pump payload below and the `/analyze` tape
 * context (`build-trade-prompt.ts`).
 */
export function capTradesToByteBudget(
  trades: readonly unknown[],
  budgetBytes: number,
): unknown[] {
  const kept: unknown[] = [];
  let remaining = budgetBytes;
  for (const trade of trades) {
    if (trade == null) continue;
    let json: string | undefined;
    try {
      json = JSON.stringify(trade);
    } catch {
      continue; // unserializable store item — skip it
    }
    if (json == null) continue;
    const cost = byteLength(json) + 1; // +1 for the joining comma
    if (cost > remaining) break;
    remaining -= cost;
    kept.push(trade);
  }
  return kept;
}

/**
 * Snapshot the latest visible trades into a gateway-request payload, capped
 * both by count (`MAX_TRADES`) and by serialized size (`MAX_PAYLOAD_BYTES`).
 * Trades are added newest-first until the next one would blow the byte
 * budget, so the snapshot stays a contiguous head of the tape. `ts` is
 * stamped at call time; `visible_count` is the untruncated total so the
 * agent knows how much was dropped. Generic over the id type so it matches
 * the trade-tape service exactly (ids are `string | number`).
 *
 * Returns a plain `Record` (the gateway `request` param type); the trade
 * objects stay opaque to the host.
 */
export function buildBlockTradeContext<Id>(
  tradeIds: readonly Id[],
  service: { selectById(id: Id): unknown },
): Record<string, unknown> {
  const payload = {
    source: 'paradigm.unified_rfqs.trade_tape',
    ts: Date.now(),
    visible_count: tradeIds.length,
    trades: [] as unknown[],
  };

  // Whatever the empty envelope costs, the trades array gets the rest.
  const budget = MAX_PAYLOAD_BYTES - byteLength(JSON.stringify(payload));
  payload.trades = capTradesToByteBudget(
    tradeIds.slice(0, MAX_TRADES).map((id) => service.selectById(id)),
    budget,
  );

  return payload;
}
